import { useState } from 'react'
import { useKpis, useSignups, useActivity } from '@/hooks/useAdminStats'
import { useAdminLocations } from '@/hooks/useAdminLocations'
import { SubscriberMap } from './SubscriberMap'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  Users, CreditCard, DollarSign, Layout, TrendingUp,
  UserPlus, ShieldCheck,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts'
import type { ActivityEvent } from '@/lib/api/admin-stats'

// ─── KPI Card ───────────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ElementType
  loading?: boolean
}

function KpiCard({ title, value, subtitle, icon: Icon, loading }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {loading ? (
              <Skeleton className="mt-1 h-8 w-24" />
            ) : (
              <p className="mt-1 text-2xl font-bold">{value}</p>
            )}
            {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Activity Feed ──────────────────────────────────────────────────────────

function ActivityFeed({ events, isLoading }: { events: ActivityEvent[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">No recent activity.</p>
  }

  return (
    <div className="space-y-3">
      {events.map((event, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
            {event.type === 'signup' ? (
              <UserPlus className="h-3.5 w-3.5 text-green-600" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm">
              {event.type === 'signup' ? (
                <><span className="font-medium">{event.email}</span> signed up</>
              ) : (
                <><Badge variant="outline" className="mr-1 text-[10px]">{event.action}</Badge> on {event.target_type} {event.target_id?.slice(0, 8)}</>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { data: kpis, isLoading: kpisLoading } = useKpis()
  const [chartDays, setChartDays] = useState(30)
  const { data: signups, isLoading: signupsLoading } = useSignups(chartDays)
  const { data: activity, isLoading: activityLoading } = useActivity(15)
  const { data: locations, isLoading: locationsLoading } = useAdminLocations()

  return (
    <div>
      <PageHeader title="Dashboard" description="HangarMax at a glance" />

      {/* KPI Cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          title="Total Users"
          value={kpis?.totalUsers ?? 0}
          subtitle={`+${kpis?.newThisWeek ?? 0} this week`}
          icon={Users}
          loading={kpisLoading}
        />
        <KpiCard
          title="Paid Subscribers"
          value={kpis?.activeSubscribers ?? 0}
          subtitle={`${kpis?.tierCounts.pilot ?? 0} Pilot, ${kpis?.tierCounts.pro ?? 0} Pro, ${kpis?.tierCounts.business ?? 0} Business`}
          icon={CreditCard}
          loading={kpisLoading}
        />
        <KpiCard
          title="MRR"
          value={`$${kpis?.mrr ?? 0}`}
          icon={DollarSign}
          loading={kpisLoading}
        />
        <KpiCard
          title="Active Layouts"
          value={kpis?.activeLayouts ?? 0}
          subtitle="Last 30 days"
          icon={Layout}
          loading={kpisLoading}
        />
        <KpiCard
          title="Trial / Expired"
          value={(kpis?.tierCounts.trial ?? 0) + (kpis?.tierCounts.expired ?? 0)}
          subtitle={kpis && kpis.totalUsers > 0
            ? `${((kpis.activeSubscribers / kpis.totalUsers) * 100).toFixed(0)}% conversion`
            : 'Conversion opportunity'}
          icon={TrendingUp}
          loading={kpisLoading}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Signup Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Signups</CardTitle>
            <div className="flex gap-1">
              <Button
                variant={chartDays === 30 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setChartDays(30)}
              >
                30d
              </Button>
              <Button
                variant={chartDays === 90 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setChartDays(90)}
              >
                90d
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {signupsLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={signups?.signups ?? []}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => d.slice(5)}
                    className="text-xs"
                  />
                  <YAxis allowDecimals={false} className="text-xs" />
                  <RechartsTooltip />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="hsl(262, 83%, 58%)"
                    fill="hsl(262, 83%, 58%)"
                    fillOpacity={0.1}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Activity Feed */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityFeed events={activity?.events ?? []} isLoading={activityLoading} />
          </CardContent>
        </Card>
      </div>

      {/* Subscriber Location Map */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Subscriber Locations</CardTitle>
        </CardHeader>
        <CardContent className="h-[400px]">
          <SubscriberMap locations={locations ?? []} isLoading={locationsLoading} />
        </CardContent>
      </Card>
    </div>
  )
}
